import { useQuery } from 'react-query';
import Environment from '@eatzy/common-reactjs/Environment';
import {
  searchCustomers,
  getCustomerDetail,
  getOrderReceipt,
  searchOrders,
} from '../../services/CustomerService';

const isFakeMode = Environment.getEnv('API_FAKE_MODE') === 'true';

/**
 * Searches for customers by phone, name, or address.
 * Disabled in fake mode or when no search fields are supplied.
 *
 * @param {{ phone?, firstName?, lastName?, address?, page? }} params
 * @returns {{ data, isLoading, error }}
 *   data is null until a search is submitted and results arrive.
 */
export const useCustomerSearch = (params = {}) => {
  const { phone, firstName, lastName, address, page } = params;

  const hasInput = !!(phone || firstName || lastName || address);

  const { data, isFetching, error } = useQuery(
    ['customer-search', phone, firstName, lastName, address, page],
    () => searchCustomers({ phone, firstName, lastName, address, page }),
    {
      enabled: !isFakeMode && hasInput,
      staleTime: 60000,
    }
  );

  return {
    data: data ?? null,
    isLoading: isFetching,
    error,
  };
};

/**
 * Fetches the full customer profile for a given customer ID.
 * Disabled in fake mode or when customerId is null or undefined.
 *
 * @param {number|string|null} customerId
 * @param {number} [page=1] — page of order history (20 per page, last 12 months)
 * @returns {{ data, isLoading, error }}
 *   data is null while loading or when customerId is not set.
 */
export const useCustomerDetail = (customerId, page = 1) => {
  const { data, isFetching, error, refetch } = useQuery(
    ['customer-detail', customerId, page],
    () => getCustomerDetail(customerId, page),
    {
      enabled: !isFakeMode && customerId != null,
      staleTime: 60000,
    }
  );

  return {
    data: data ?? null,
    isLoading: isFetching,
    error,
    refetch,
  };
};

/**
 * Fetches an order receipt by order ID or display ID.
 * All-digit strings are resolved as a numeric order ID by the API;
 * any other string is resolved as a display ID (e.g. "ABC-123").
 * Disabled in fake mode or when q is empty or null.
 *
 * @param {string|null} q
 * @returns {{ data, isLoading, error }}
 *   data is null while loading or when q is not set.
 */
export const useOrderReceipt = (q) => {
  const hasInput = !!(q && q.trim().length > 0);

  const { data, isFetching, error, refetch } = useQuery(
    ['order-receipt', q],
    () => getOrderReceipt(q),
    {
      enabled: !isFakeMode && hasInput,
      staleTime: 60000,
    }
  );

  return {
    data: data ?? null,
    isLoading: isFetching,
    error,
    refetch,
  };
};

/**
 * Searches for orders by date/time range and optional restaurant name.
 * Disabled in fake mode or when neither from nor to is provided.
 *
 * @param {{ from?, to?, restaurant?, page? }} params
 *   from, to — Unix timestamps in seconds; at least one required by the API
 *   restaurant — optional partial name match (case-insensitive substring)
 *   page — page number (default 1)
 * @returns {{ data, isLoading, error }}
 *   data is null until valid params are supplied and results arrive.
 */
export const useOrderSearch = (params = {}) => {
  const { from, to, restaurant, page } = params;

  const hasInput = from != null || to != null;

  const { data, isFetching, error } = useQuery(
    ['order-search', from, to, restaurant, page],
    () => searchOrders({ from, to, restaurant, page }),
    {
      enabled: !isFakeMode && hasInput,
      staleTime: 60000,
    }
  );

  return {
    data: data ?? null,
    isLoading: isFetching,
    error,
  };
};
